/**
 * Single source of truth for "after auth, where does this user belong?"
 */

import type { User } from '@supabase/supabase-js';
import type { ApprovalStatus, AgencyRole } from '@/contexts/AgencyContext';

export type AppRole = 'super_admin' | 'agency_staff' | 'brand_client' | 'creator' | null;

export type AgencyApprovalState =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'suspended'
  | 'disabled'
  | 'archived';

export interface LandingContext {
  user: User | null;
  role: AppRole;
  isSuperAdmin: boolean;
  agencyId: string | null;
  agencyRole: AgencyRole;
  onboardingComplete: boolean;
  /** Retained for backward-compat with callers; no longer gates routing. */
  approvalStatus: ApprovalStatus | null;
  /** Platform approval state for the user's agency. */
  agencyApprovalState?: AgencyApprovalState | null;
}

export function resolveLandingRoute(ctx: LandingContext): string {
  if (!ctx.user) return '/login';
  if (!ctx.user.email_confirmed_at) return '/verify-email';
  if (ctx.isSuperAdmin) return '/superadmin';

  if (ctx.role === 'brand_client') return '/portal';
  if (ctx.role === 'creator') return '/creator';

  if (!ctx.agencyId || !ctx.onboardingComplete) return '/onboarding';

  // Platform approval gate — every non-approved agency lands on the
  // Pending Review screen regardless of onboarding progress.
  if (ctx.agencyApprovalState && ctx.agencyApprovalState !== 'approved') {
    return '/pending-review';
  }

  return '/dashboard';
}
